import {
  CloudDownload,
  Search as SearchIcon,
  Add as AddIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Chip,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip,
  MenuItem,
  InputAdornment,
  Button,
  Table as MuiTable,
  styled,
  SxProps,
  Box,
  TextField,
  Typography,
  Collapse,
  ButtonPropsColorOverrides,
  Autocomplete,
  autocompleteClasses,
} from "@mui/material";
import { Formik, Form } from "formik";
import { DateTime } from "luxon";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import Papa from "papaparse";
import { ReactElement, ReactNode, useRef, useState } from "react";
import React from "react";
import { FormattedMessage } from "react-intl";
import * as Yup from "yup";

import theme, { palette } from "~frontend/theme";
import downloadContent from "~frontend/util/downloadContent";

import { Checkbox } from "./Checkbox";
import { ButtonWrapper, ControlsPaper, ControlsRow } from "./ControlsPaper";
import { FlexBox } from "./FlexBox";
import { InternalLink, LinkButton } from "./Link";
import { SecondaryDropdown, SecondaryMultiSelect } from "./SecondaryDropdown";
import { SecondaryTextField } from "./SecondaryTextField";
import TooltipButton from "./TooltipButton";
import { DateRangePicker } from "./forms";
import { MultiSelectValue } from "./forms/FormSelect";
import { FormikTextField } from "./forms/FormTextField";

type RowActionButton<R extends { id: string }> = {
  id: string;
  icon: React.ReactNode;
  tooltipLabel?: string;
  onClick: (row: R) => void | Promise<void>;
  confirm?: ConfirmOptions | ((row: R) => ConfirmOptions);
  disabled?: boolean;
  type?: "button";
  sx?: SxProps;
};

type RowActionButtonWithLabel<R extends { id: string }> = Omit<
  RowActionButton<R>,
  "icon"
> & {
  icon?: React.ReactNode;
  label: ReactNode | string;
  variant?: "text" | "contained" | "outlined";
  color?: "primary" | "secondary" | keyof ButtonPropsColorOverrides;
};

type RowActionLink<R extends { id: string }> = {
  id: string;
  icon: React.ReactNode;
  tooltipLabel?: string;
  href: string | ((row: R) => string);
  type: "link";
  target?: "_blank";
};

type RowActionPlaceholder = {
  id: string;
  label: ReactNode;
  type: "placeholder";
};

const TableWithoutHorizontalPadding = styled(MuiTable)`
  th:first-of-type {
    padding-left: 0;
  }

  th:last-of-type {
    padding-right: 0;
  }

  tr td:first-of-type {
    padding-left: 0;
  }

  tr td:last-of-type {
    padding-right: 0;
  }
`;

const ROW_BORDER = `1px solid ${palette.midGray}`;

export type RowAction<R extends { id: string }> =
  | RowActionButton<R>
  | RowActionButtonWithLabel<R>
  | RowActionLink<R>
  | RowActionPlaceholder;

export type RowActions<R extends { id: string }> = RowAction<R>[];

export type TableColumnAlignment = "left" | "right" | "center";

export type TableColumns<R extends { id: string }> = {
  key: string;
  title: React.ReactNode;
  sx?: SxProps;
  // The first column will always be align: left, the rest will be center by default unless
  // we are right-aligning after a certain index (see `alignRightAfterIdx` Table prop)
  align?: TableColumnAlignment;
  fullWidth?: (row: R) => boolean;
  renderValue: (row: R, ctx: { parent?: R }) => React.ReactNode;
}[];

export type TableBaseProps<R extends { id: string }> = {
  size?: "medium" | "small";
  columns: TableColumns<R>;
  rows: R[];
  rowActions?: RowActions<R> | ((row: R) => RowActions<R>);
  emptyState?: React.ReactNode;
  selectedRows?: string[] | null;
  alignRightAfterIdx?: number;
  actionSx?: SxProps;
  onChangeSelectedRows?: (selectedRows: string[]) => void;
  selectDisabled?: boolean;
  selectAllDisabled?: boolean;
  rowActionsLabel?: React.ReactNode;
  containerSx?: SxProps;
  collapsibleContent?: (row: R) => React.ReactNode;
  stickyHeader?: boolean;
  noFooter?: boolean;
  footer?: React.ReactNode;
  footerActions?: React.ReactNode;
  getChildRows?: (row: R) => R[];
  childRowSelectionMode?: "parentOnly" | "childOnly" | "both";
  "data-testid"?: string;
};

export type TablePropsWithPaginationFooter<R extends { id: string }> =
  TableBaseProps<R> & {
    noFooter?: boolean;
    footer?: undefined;
    totalCount: number;
    rowsPerPage: number;
    onChangeRowsPerPage: (rowsPerPage: number) => void;
    page: number;
    onChangePage: (page: number) => void;
    footerActions?: React.ReactNode;
    reverseFooterOrder?: boolean;
  };

export type TablePropsWithCustomFooter<R extends { id: string }> =
  TableBaseProps<R> & {
    noFooter?: boolean;
    footer: NonNullable<React.ReactNode>;
  };

export type TablePropsWithoutFooter<R extends { id: string }> =
  TableBaseProps<R> & {
    noFooter: true;
    footer?: undefined;
  };

export type TableProps<R extends { id: string }> =
  | TablePropsWithPaginationFooter<R>
  | TablePropsWithCustomFooter<R>
  | TablePropsWithoutFooter<R>;

function RowDataColumns<R extends { id: string }>({
  row,
  parent,
  columns,
  alignRightAfterIdx,
  cellSx,
}: {
  row: R;
  parent?: R;
  columns: TableColumns<R>;
  alignRightAfterIdx?: number;
  cellSx?: SxProps;
}) {
  if (columns[0].fullWidth?.(row)) {
    return (
      <TableCell
        key={columns[0].key}
        colSpan={columns.length}
        sx={
          {
            ...cellSx,
            ...(columns[0].sx || {}),
          } as SxProps
        }
      >
        {columns[0].renderValue(row, { parent })}
      </TableCell>
    );
  }

  return (
    <>
      {columns.map(({ key, align = "center", sx = {}, renderValue }, idx) => {
        return (
          <TableCell
            key={key}
            align={
              alignRightAfterIdx && idx > alignRightAfterIdx
                ? "right"
                : idx === 0
                  ? "left"
                  : align
            }
            sx={{ ...cellSx, ...sx } as SxProps}
          >
            {renderValue(row, { parent })}
          </TableCell>
        );
      })}
    </>
  );
}

function Row<R extends { id: string }>({
  row,
  size,
  collapsibleContent,
  selectedRows,
  checkboxesEnabled,
  selectDisabled,
  rowActions,
  handleSelectClick,
  columns,
  alignRightAfterIdx,
  actionSx,
  renderRowAction,
  getChildRows,
  childRowSelectionMode = "parentOnly",
}: {
  row: R;
  collapsibleContent?: (row: R) => ReactNode;
  selectedRows?: string[] | null;
  checkboxesEnabled?: boolean;
  selectDisabled?: boolean;
  rowActions?: RowActions<R> | ((row: R) => RowActions<R>);
  handleSelectClick: (id: string) => void;
  size?: "medium" | "small";
  columns: TableColumns<R>;
  alignRightAfterIdx?: number;
  actionSx?: SxProps;
  renderRowAction: (rowAction: RowAction<R>, row: R) => ReactElement;
  getChildRows?: (row: R) => R[];
  childRowSelectionMode?: "parentOnly" | "childOnly" | "both";
}): ReactNode {
  const [open, setOpen] = useState(false);
  const childRows = getChildRows?.(row) || [];

  const dataCellSx = {
    borderBottom: childRows.length > 0 ? 0 : ROW_BORDER,
  };

  const controlCellSx = {
    ...dataCellSx,
    ...(size === "small" ? { p: 0 } : {}),
  };

  return (
    <React.Fragment key={row.id}>
      <TableRow
        key={row.id}
        sx={{
          backgroundColor:
            selectedRows && selectedRows.includes(row.id)
              ? palette.lightGray
              : "",
        }}
      >
        {collapsibleContent && (
          <TableCell sx={controlCellSx}>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
        {checkboxesEnabled && selectedRows != null && (
          <TableCell sx={controlCellSx}>
            {childRows.length > 0 && childRowSelectionMode === "childOnly" ? (
              // Empty cell for parent row checkbox alignment
              <></>
            ) : (
              <Checkbox
                disabled={selectDisabled}
                checked={selectedRows.includes(row.id)}
                onChange={() => handleSelectClick(row.id)}
              />
            )}
          </TableCell>
        )}
        <RowDataColumns
          row={row}
          columns={columns}
          alignRightAfterIdx={alignRightAfterIdx}
          cellSx={dataCellSx}
        />
        {rowActions ? (
          <TableCell
            key="actions"
            align="right"
            sx={{ ...controlCellSx, ...actionSx }}
          >
            {(typeof rowActions === "function"
              ? rowActions(row)
              : rowActions
            ).map(rowAction => renderRowAction(rowAction, row))}
          </TableCell>
        ) : null}
      </TableRow>
      {collapsibleContent && (
        <TableRow sx={{ borderBottom: 0 }}>
          <TableCell
            sx={{ width: "32px", pb: `0 !important`, borderBottom: 0 }}
          />
          <TableCell
            sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 0 }}
            colSpan={columns.length + (checkboxesEnabled ? 1 : 0) + 1}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>{collapsibleContent(row)}</Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      {childRows?.map((childRow, idx) => {
        const dataCellSx = {
          // Last child row gets bottom border to serve as the bottom border for the whole group
          borderBottom: idx === childRows.length - 1 ? ROW_BORDER : 0,
          paddingTop: 0,
        };

        const controlCellSx = {
          ...dataCellSx,
          ...(size === "small" ? { p: 0 } : {}),
        };

        return (
          <TableRow>
            {checkboxesEnabled && selectedRows != null && (
              <TableCell
                sx={{
                  ...controlCellSx,
                  width: "42px",
                }}
              >
                {childRowSelectionMode === "parentOnly" ? (
                  // Empty cell for parent row checkbox alignment
                  <></>
                ) : (
                  <Checkbox
                    disabled={selectDisabled}
                    checked={selectedRows.includes(childRow.id)}
                    onChange={() => handleSelectClick(childRow.id)}
                  />
                )}
              </TableCell>
            )}
            <RowDataColumns
              row={childRow}
              parent={row}
              columns={columns}
              alignRightAfterIdx={alignRightAfterIdx}
              cellSx={dataCellSx}
            />
            {rowActions ? (
              <TableCell
                key="actions"
                align="right"
                sx={{ ...controlCellSx, ...actionSx }}
              >
                {/* Empty cell for child row actions alignment */}
              </TableCell>
            ) : null}
          </TableRow>
        );
      })}
    </React.Fragment>
  );
}

export default function Table<R extends { id: string }>(
  props: TableProps<R>
): ReactElement {
  const {
    size,
    columns,
    rows,
    rowActions,
    rowActionsLabel,
    emptyState,
    noFooter,
    selectedRows,
    alignRightAfterIdx,
    actionSx,
    onChangeSelectedRows,
    selectDisabled,
    selectAllDisabled,
    containerSx,
    stickyHeader,
  } = props;

  const headerRef = useRef<HTMLTableSectionElement>(null);

  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  function renderRowAction(rowAction: RowAction<R>, row: R): ReactElement {
    if (rowAction.type === "placeholder") {
      return (
        <Chip key={rowAction.id} label={rowAction.label} variant="outlined" />
      );
    }

    if (rowAction.type === "link") {
      return <ActionLink key={rowAction.id} row={row} rowAction={rowAction} />;
    }

    return (
      <ActionButton
        key={rowAction.id}
        row={row}
        rowAction={rowAction}
        loading={loadingIds.has(row.id)}
        sx={{ color: palette.navy, ...rowAction.sx }}
        setLoading={loading => {
          const newSet = new Set(loadingIds);
          if (loading) {
            newSet.add(row.id);
          } else {
            newSet.delete(row.id);
          }
          setLoadingIds(newSet);
        }}
      />
    );
  }

  const checkboxesEnabled =
    selectedRows != null && onChangeSelectedRows != null && rows.length > 0;

  function handleSelectAllClick() {
    if (!checkboxesEnabled) {
      return;
    }

    if (selectedRows.length === rows.length) {
      onChangeSelectedRows?.([]);
    } else {
      onChangeSelectedRows?.(rows.map(r => r.id));
    }
  }

  function handleSelectClick(id: string) {
    if (!checkboxesEnabled) {
      return;
    }

    if (selectedRows.includes(id)) {
      onChangeSelectedRows?.(selectedRows.filter(rowId => rowId !== id));
    } else {
      onChangeSelectedRows?.([...selectedRows, id]);
    }
  }

  const columnCount =
    columns.length + (rowActions ? 1 : 0) + (checkboxesEnabled ? 1 : 0);

  let actionsColspan = 0;
  if (!props.noFooter && !props.footer && props.footerActions) {
    if (checkboxesEnabled && columnCount > 2) {
      actionsColspan = 2;
    } else {
      actionsColspan = 1;
    }
  }

  const paginationColspan = columnCount - actionsColspan;

  // Will only scroll if the top of the header is above the viewport
  const scrollToHeader = () => {
    if (!headerRef.current || !rows || rows.length === 0) {
      return;
    }

    const rect = headerRef.current.getBoundingClientRect();
    if (rect.top < 0) {
      const elementTop =
        headerRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementTop - 50, behavior: "smooth" });
    }
  };

  const footer = () => {
    if (noFooter) {
      return null;
    }

    if (props.footer !== null && props.footer !== undefined) {
      return props.footer;
    }

    if (props.totalCount <= 10) {
      // If there are less than 10 rows, don't show pagination buttons.'
      return (
        <TableFooter>
          <TableRow sx={{ pb: 0 }}>
            <TableCell
              key="footer-actions"
              colSpan={columnCount}
              sx={{ borderBottom: 0, textAlign: "right" }}
            >
              {props.footerActions}
            </TableCell>
          </TableRow>
        </TableFooter>
      );
    }

    const footerActions = props.footerActions && (
      <TableCell
        key="footer-actions"
        colSpan={actionsColspan}
        sx={{ borderBottom: 0, padding: 0 }}
      >
        {props.footerActions}
      </TableCell>
    );

    const pagination = (
      <TablePagination
        key="pagination"
        rowsPerPageOptions={[10, 25, 50, 100]}
        colSpan={paginationColspan}
        count={props.totalCount}
        rowsPerPage={props.rowsPerPage}
        page={props.page}
        onPageChange={(_evt, newPage) => {
          props.onChangePage(newPage);
          scrollToHeader();
        }}
        onRowsPerPageChange={evt =>
          props.onChangeRowsPerPage(parseInt(evt.target.value, 10))
        }
        labelRowsPerPage={
          <FormattedMessage
            defaultMessage="Rows per page"
            description="Label for the number of rows per table page"
          />
        }
        labelDisplayedRows={({ from, to, count }) => (
          <FormattedMessage
            defaultMessage="{from}-{to} of {count}"
            description="Pagination display (e.g. results 1-10 of 50)"
            values={{ from, to, count }}
          />
        )}
        sx={{
          borderBottom: 0,
          "& .MuiTablePagination-toolbar": { pl: 0 },
          "& .MuiTablePagination-input": {
            mr: 3,
            borderBottom: `1px solid ${palette.midGray}`,
          },
          "& .MuiTablePagination-actions": {
            ml: 2,
            "> .MuiButtonBase-root": {
              px: 0,
            },
          },
        }}
      />
    );

    return (
      <TableFooter>
        <TableRow sx={{ pb: 0 }}>
          {props.reverseFooterOrder
            ? [pagination, footerActions]
            : [footerActions, pagination]}
        </TableRow>
      </TableFooter>
    );
  };

  return (
    <TableContainer sx={containerSx} data-testid={props["data-testid"]}>
      <TableWithoutHorizontalPadding
        size={size || "medium"}
        stickyHeader={stickyHeader}
      >
        <TableHead
          ref={headerRef}
          sx={{
            "& .MuiTableCell-root": {
              verticalAlign: "bottom",
              textWrap: "nowrap",
              pb: 1,
              lineHeight: "120%",
              color: palette.darkGray,
            },

            borderBottom: `2px solid ${palette.lightGray}`,
          }}
        >
          <TableRow>
            {checkboxesEnabled && (
              <TableCell sx={{ width: "32px", pb: `0 !important` }}>
                <Checkbox
                  checked={selectedRows.length === rows.length}
                  onChange={handleSelectAllClick}
                  disabled={selectDisabled}
                  sx={{
                    paddingBottom: 0.5,
                    ...(selectAllDisabled ? { display: "none" } : {}),
                  }}
                />
              </TableCell>
            )}
            {props.collapsibleContent && (
              <TableCell sx={{ width: "32px", pb: `0 !important` }} />
            )}
            {columns.map(({ key, align = "center", title, sx }, idx) => (
              <TableCell
                key={key}
                sx={{
                  color: palette.darkGray,
                  fontWeight: 600,
                  ...sx,
                }}
                align={
                  alignRightAfterIdx && idx > alignRightAfterIdx
                    ? "right"
                    : idx === 0
                      ? "left"
                      : align
                }
              >
                {title}
              </TableCell>
            ))}
            {rowActions ? (
              <TableCell key="actions" align="right" sx={actionSx}>
                {rowActionsLabel || "Actions"}
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && emptyState ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            rows.map(row => {
              return (
                <Row<R>
                  {...props}
                  key={row.id}
                  row={row}
                  handleSelectClick={handleSelectClick}
                  renderRowAction={renderRowAction}
                  checkboxesEnabled={checkboxesEnabled}
                />
              );
            })
          )}
        </TableBody>
        {footer()}
      </TableWithoutHorizontalPadding>
    </TableContainer>
  );
}

const ACTION_ICON_SIZE = 16;

function ActionButton<R extends { id: string }>({
  row,
  rowAction,
  loading,
  setLoading,
  sx = {},
}: {
  row: R;
  rowAction: RowActionButton<R> | RowActionButtonWithLabel<R>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  sx?: SxProps;
}) {
  const confirm = useConfirm();

  const handleClick = async () => {
    if (rowAction.confirm) {
      const confirmOptions =
        typeof rowAction.confirm === "function"
          ? rowAction.confirm(row)
          : rowAction.confirm;
      const { confirmed } = await confirm(confirmOptions);
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    await rowAction.onClick(row);
    setLoading(false);
  };

  if (loading) {
    return <CircularProgress size={ACTION_ICON_SIZE} />;
  }

  const isLabelButton = "label" in rowAction;

  const button = isLabelButton ? (
    <Button
      onClick={handleClick}
      disabled={rowAction.disabled}
      sx={sx}
      data-testid={`table-button-${rowAction.id}-${row.id}`}
      variant={rowAction.variant}
      color={rowAction.color ?? "primary"}
    >
      {rowAction.icon && <>{rowAction.icon}</>}
      {rowAction.label}
    </Button>
  ) : (
    <IconButton
      onClick={handleClick}
      data-testid={`table-button-${rowAction.id}-${row.id}`}
      disabled={rowAction.disabled}
      sx={{
        "& .MuiSvgIcon-root": {
          height: ACTION_ICON_SIZE,
          width: ACTION_ICON_SIZE,
        },
        ...sx,
      }}
    >
      {rowAction.icon}
    </IconButton>
  );

  if (rowAction.tooltipLabel) {
    return <Tooltip title={rowAction.tooltipLabel}>{button}</Tooltip>;
  }

  return button;
}

function ActionLink<R extends { id: string }>({
  row,
  rowAction,
}: {
  row: R;
  rowAction: RowActionLink<R>;
}) {
  const to =
    typeof rowAction.href === "function" ? rowAction.href(row) : rowAction.href;

  const sx: SxProps = {
    "& svg": {
      color: palette.navy,
      width: "16px",
      height: "16px",
    },
  };

  const link =
    rowAction.target === "_blank" ? (
      <IconButton
        component="a"
        href={to}
        target="_blank"
        data-testid={`table-button-${rowAction.id}-${row.id}`}
        sx={sx}
      >
        {rowAction.icon}
      </IconButton>
    ) : (
      <IconButton
        component={InternalLink}
        to={to}
        data-testid={`table-button-${rowAction.id}-${row.id}`}
        sx={sx}
      >
        {rowAction.icon}
      </IconButton>
    );

  if (rowAction.tooltipLabel) {
    return <Tooltip title={rowAction.tooltipLabel}>{link}</Tooltip>;
  }

  return link;
}

export const HelpText = styled("div")`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  color: ${palette.darkGray};
`;

export type Filter = {
  id: string;
  label: React.ReactNode;
  type?: "dropdown" | "number" | "dateRange" | "autocomplete";
} & (
  | ({
      type?: "dropdown";
      options: { value: string; label: React.ReactNode }[];
    } & (
      | {
          value: string;
          onChange: (value: string) => void;
          multi?: false;
        }
      | {
          value: string[];
          onChange: (value: string[]) => void;
          multi: true;
        }
    ))
  | {
      type: "number";
      unit: string;
      value: number;
      onChange: (value: number) => void;
    }
  | {
      type: "dateRange";
      maxDate?: DateTime;
      minDate?: DateTime;
      value: { startDate: DateTime; endDate: DateTime } | null;
      onChange: (
        value: { startDate: DateTime; endDate: DateTime } | null
      ) => void;
    }
  | ({
      type: "autocomplete";
      search: string;
      onChangeSearch: (newSearch: string) => void;
      options: string[];
      optionsLoading?: boolean;
      optionsLoadingText?: string;
      noOptionsText?: string;
    } & (
      | {
          value: string;
          onChange: (value: string) => void;
          multi?: false;
        }
      | {
          value: string[];
          onChange: (value: string[]) => void;
          multi: true;
        }
    ))
);

type ButtonProps = {
  id: string;
  label: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltipTitle?: string;
  sx?: SxProps;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | keyof ButtonPropsColorOverrides;
};

export type AddButtonProps = ButtonProps;
type ExportButtonProps = Omit<ButtonProps, "onClick"> & {
  getData: () => Promise<{
    data: Record<string, any>[];
    fileName: string;
    options: Papa.UnparseConfig;
  }>;
};

export type ActionButtonProps = Omit<ButtonProps, "onClick"> & {
  loadingLabel?: React.ReactNode;
  onClick: () => void | Promise<void>;
  confirm?: ConfirmOptions;
};

export type DeleteManyState =
  | {
      selecting: false;
    }
  | {
      selecting: true;
      selectedIds: string[];
      deleting: boolean;
    };

export function TableControls({
  addButtons,
  addSearchButtons,
  exportButtons,
  actionButtons,
  secondaryButtons,
  filters,
  sort,
  search,
  deleteMany,
  helpText,
  controlsFooter,
  paperStyles,
  variant = "standard",
}: {
  addButtons?: AddButtonProps[];
  addSearchButtons?: AddButtonProps[];
  exportButtons?: ExportButtonProps[];
  actionButtons?: ActionButtonProps[];
  secondaryButtons?: ButtonProps[];
  filters?: Filter[];
  sort?: {
    value: string;
    onChange: (value: string) => void;
    options: Readonly<{ value: string; label: React.ReactNode }[]>;
  };
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    schema?: Yup.StringSchema;
    sx?: SxProps;
  };
  deleteMany?: {
    deleteManyState: DeleteManyState;
    setDeleteManyState: (newState: DeleteManyState) => void;
    onDeleteSelected: () => void;
    deleteMultipleButtonText: string;
    deleteSelectedButtonText: string;
  };
  helpText?: string;
  controlsFooter?: React.ReactNode;
  paperStyles?: SxProps;
  variant?: "standard" | "compact";
}): ReactElement {
  const hasAtLeastOneButton = !!(
    addButtons?.length ||
    exportButtons?.length ||
    actionButtons?.length ||
    secondaryButtons?.length ||
    addSearchButtons?.length
  );

  const hasAtLeastOneControl =
    filters || sort || search || deleteMany || helpText || controlsFooter;

  const searchFormik = search && (
    <Formik
      enableReinitialize={true}
      initialValues={{ search: search.value }}
      validationSchema={Yup.object().shape({
        search: search.schema ?? Yup.string(),
      })}
      onSubmit={values => search.onChange(values.search)}
    >
      {({ submitForm, errors }) => (
        <Form
          style={{ flex: 1 }}
          onKeyDownCapture={event => {
            // This is a nested form in some contexts so it needs
            // some custom handling for the enter key
            if (event.key === "Enter") {
              event.preventDefault();
              return submitForm();
            }
          }}
        >
          <FormikTextField
            fullWidth
            name="search"
            onBlur={submitForm}
            placeholder={search.placeholder}
            sx={{ pt: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={submitForm}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                border: `1px solid ${palette.midGray} !important`,
                fontSize: "12px",
                ...search.sx,
              },
            }}
            error={!!errors.search}
            helperText={errors?.search}
            inputProps={{
              "data-testid": "table-search-input",
            }}
          />
        </Form>
      )}
    </Formik>
  );

  return (
    <ControlsPaper
      elevation={0}
      sx={{
        ...paperStyles,
        ...(hasAtLeastOneButton && !hasAtLeastOneControl
          ? { backgroundColor: "transparent", px: 0 }
          : {}),
        ...(variant === "compact" ? { pt: 1 } : {}),
      }}
    >
      <ControlsRow>
        <FlexBox
          sx={{
            gap: theme.spacing(2),
            width:
              hasAtLeastOneButton || variant === "compact" ? undefined : "100%",
            justifyContent: hasAtLeastOneControl
              ? "flex-start"
              : "space-between",
            alignItems: variant === "compact" ? "center" : "flex-end",
            mr: 2,
          }}
        >
          {filters?.map(filter => (
            <TableFilter
              key={filter.id}
              filter={filter}
              fullWidth={!hasAtLeastOneButton}
            />
          ))}
          {sort ? (
            <SecondaryDropdown
              variant="underlined"
              label="Sort By"
              value={sort.value}
              sx={{ minWidth: "200px" }}
              onChange={event => sort.onChange(event.target.value)}
              fullWidth={!hasAtLeastOneButton}
            >
              {sort.options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </SecondaryDropdown>
          ) : null}
          {addSearchButtons?.map(addSearchButtonProps => (
            <Button
              {...addSearchButtonProps}
              key={addSearchButtonProps.id}
              variant="link"
              disableFocusRipple={true}
              sx={{ padding: 0 }}
            >
              {addSearchButtonProps.label}
            </Button>
          ))}
        </FlexBox>
        {hasAtLeastOneButton ? (
          <ButtonWrapper>
            {deleteMany ? <DeleteManyControls {...deleteMany} /> : null}
            {secondaryButtons?.map(secondaryButtonProps => (
              <AddButton
                {...secondaryButtonProps}
                variant="contained"
                color="secondary"
                key={secondaryButtonProps.id}
              />
            ))}
            {addButtons?.map(addButtonProps => (
              <AddButton {...addButtonProps} key={addButtonProps.id} />
            ))}
            {exportButtons?.map(exportButtonProps => (
              <ExportButton {...exportButtonProps} key={exportButtonProps.id} />
            ))}
            {actionButtons?.map(actionButtonProps => (
              <TableActionButton
                {...actionButtonProps}
                key={actionButtonProps.id}
              />
            ))}
          </ButtonWrapper>
        ) : null}
        {search && variant === "compact" && (
          <ControlsRow sx={{ flex: 1 }}>{searchFormik}</ControlsRow>
        )}
      </ControlsRow>
      {search && variant === "standard" ? (
        <ControlsRow>{searchFormik}</ControlsRow>
      ) : null}

      {helpText ? (
        <ControlsRow>
          <HelpText>{helpText}</HelpText>
        </ControlsRow>
      ) : null}
      {controlsFooter && <>{controlsFooter}</>}
    </ControlsPaper>
  );
}

function TableFilter({
  filter,
  fullWidth,
}: {
  filter: Filter;
  fullWidth?: boolean;
}): ReactElement {
  switch (filter.type) {
    case undefined:
    case "dropdown":
      return filter.multi ? (
        <SecondaryMultiSelect
          key={filter.id}
          label={filter.label}
          value={filter.value}
          onChange={event => filter.onChange(event.target.value)}
          sx={{ minWidth: "200px" }}
          renderValue={selectedUnknown => (
            <MultiSelectValue
              selected={selectedUnknown as string[]}
              options={filter.options}
            />
          )}
          fullWidth={fullWidth}
        >
          {filter.options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SecondaryMultiSelect>
      ) : (
        <SecondaryDropdown
          key={filter.id}
          label={filter.label}
          value={filter.value}
          onChange={event => filter.onChange(event.target.value)}
          sx={{ minWidth: "200px" }}
          fullWidth={fullWidth}
        >
          {filter.options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SecondaryDropdown>
      );
    case "number":
      return (
        <>
          <TextField
            variant="standard"
            label={filter.label}
            inputProps={{
              type: "number",
              min: 1,
              max: 365,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    variant="body2"
                    sx={{ paddingTop: "7px", color: palette.darkestBlue }}
                  >
                    {filter.unit}
                  </Typography>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: "14px" } }}
            sx={{
              minWidth: "200px",
              "& .MuiInputBase-root": {
                fontSize: "12px",
                padding: "1px 0 2px",
                "::before": {
                  borderBottom: `1px solid ${palette.midGray}`,
                },
              },
              "& .Mui-focused::after": {
                borderBottom: `2px solid ${palette.darkGray}`,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: palette.darkGray,
              },
              "& .MuiInputBase-input": {
                padding: "7px 0 2px",
              },
            }}
            fullWidth={fullWidth}
            value={String(filter.value)}
            onChange={e => filter.onChange(parseInt(e.target.value))}
          />
        </>
      );
    case "dateRange":
      return (
        <DateRangePicker
          value={filter.value}
          onChange={filter.onChange}
          label={filter.label}
          maxDate={filter.maxDate}
          minDate={filter.minDate}
          fullWidth={fullWidth}
          secondary
        />
      );
    case "autocomplete":
      return (
        <Autocomplete
          renderInput={params => (
            <SecondaryTextField
              {...params}
              label={filter.label}
              sx={{
                minWidth: "200px",
              }}
              slotProps={{
                inputLabel: {
                  ...params.InputLabelProps,
                  shrink: true,
                },
                input: {
                  ...params.InputProps,
                  sx: {
                    // to match other filter fields
                    height: "29.5px",

                    // prevents search icon from flipping on focus
                    [`& .${autocompleteClasses.popupIndicator}`]: {
                      transform: "none",
                    },
                  },
                },
              }}
            />
          )}
          popupIcon={
            <InputAdornment position="end">
              <SearchIcon sx={{ height: "20px" }} />
            </InputAdornment>
          }
          multiple={filter.multi}
          inputValue={filter.search}
          onInputChange={(event, newSearch) => {
            if (event?.type === "change" || event?.type === "blur") {
              filter.onChangeSearch(newSearch);
            }
          }}
          options={filter.options}
          filterOptions={options => options}
          loading={filter.optionsLoading}
          loadingText={filter.optionsLoadingText}
          noOptionsText={filter.noOptionsText}
          value={filter.value}
          onChange={(_event, newValue) => {
            if (filter.multi) {
              filter.onChange(newValue as string[]);
            } else {
              filter.onChange(newValue as string);
            }
          }}
          renderValue={value => {
            let valueText: ReactNode;

            if (filter.multi) {
              const valueArr = value as string[];
              switch (valueArr.length) {
                case 0:
                  return null;
                case 1:
                  valueText = valueArr[0];
                  break;
                default:
                  valueText = (
                    <FormattedMessage
                      defaultMessage="{firstValue} and {numOthers} more"
                      description="Autocomplete value with multiple options selected"
                      values={{
                        firstValue: valueArr[0],
                        numOthers: valueArr.length - 1,
                      }}
                    />
                  );
                  break;
              }
            } else {
              if (!value) {
                return null;
              }
              valueText = value;
            }

            return (
              <Typography variant="body2" sx={{ marginRight: 1 }}>
                {valueText}
              </Typography>
            );
          }}
          fullWidth={fullWidth}
        />
      );
  }
}

function TableButton({
  label,
  onClick,
  icon,
  disabled,
  tooltipTitle,
  id,
  sx = {},
  variant = "contained",
  color = "primary",
}: ButtonProps) {
  return (
    <TooltipButton
      tooltipText={tooltipTitle}
      onClick={onClick}
      variant={variant}
      color={color}
      sx={{ maxWidth: 250, ...sx }}
      startIcon={icon}
      disabled={disabled}
      data-testid={`table-button-${id}`}
    >
      {label}
    </TooltipButton>
  );
}

export function AddButton(props: AddButtonProps): ReactElement {
  return <TableButton icon={props.icon || <AddIcon />} {...props} />;
}

function ExportButton({
  label,
  getData,
  icon,
  disabled,
  tooltipTitle,
  id,
}: ExportButtonProps) {
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();

  async function onClick() {
    setLoading(true);

    try {
      const { data, fileName, options } = await getData();
      const csvContent = Papa.unparse(data, options);

      downloadContent(csvContent, { fileName, contentType: "text/csv" });
    } catch (e: any) {
      console.error(e);
      snackbar.enqueueSnackbar(e.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <TableButton
      icon={icon || <CloudDownload />}
      onClick={onClick}
      disabled={loading || disabled}
      label={label}
      tooltipTitle={tooltipTitle}
      id={id}
    />
  );
}

function TableActionButton({
  loadingLabel,
  label,
  icon,
  disabled,
  tooltipTitle,
  id,
  onClick,
  confirm: confirmOptions,
}: ActionButtonProps) {
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  const handleClick = async () => {
    if (confirmOptions) {
      const { confirmed } = await confirm(confirmOptions);
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);

    try {
      await onClick();
    } catch (e: unknown) {
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <TableButton
      icon={icon}
      onClick={handleClick}
      disabled={loading || disabled}
      label={loading && loadingLabel ? loadingLabel : label}
      tooltipTitle={tooltipTitle}
      id={id}
    />
  );
}

function DeleteManyControls({
  deleteManyState,
  setDeleteManyState,
  onDeleteSelected,
  deleteMultipleButtonText,
  deleteSelectedButtonText,
}: {
  deleteManyState: DeleteManyState;
  setDeleteManyState: (newState: DeleteManyState) => void;
  onDeleteSelected: () => void;
  deleteMultipleButtonText: string;
  deleteSelectedButtonText: string;
}): ReactElement {
  if (deleteManyState.selecting) {
    return (
      <FlexBox direction="column" alignItems="flex-start">
        <LinkButton onClick={() => setDeleteManyState({ selecting: false })}>
          Cancel
        </LinkButton>
        <Button
          onClick={onDeleteSelected}
          variant="contained"
          color="tertiary"
          sx={{ backgroundColor: palette.midGray, color: palette.navy }}
          disabled={deleteManyState.selectedIds.length === 0}
        >
          {deleteSelectedButtonText}
        </Button>
      </FlexBox>
    );
  } else {
    return (
      <Button
        onClick={() =>
          setDeleteManyState({
            selecting: true,
            selectedIds: [],
            deleting: false,
          })
        }
        variant="contained"
        color="tertiary"
        sx={{ backgroundColor: palette.midGray, color: palette.navy }}
      >
        {deleteMultipleButtonText}
      </Button>
    );
  }
}

export const TableBox = styled(Box)({
  padding: `${theme.spacing(2)} ${theme.spacing(5)} ${theme.spacing(4)}`,
  backgroundColor: palette.white,
});
